// Money, shares and weights for the fund pages (projects/investing §4, §5).
//
// The API sends every amount as a decimal string ("10234.56", a price to six
// places, shares to eight). They're formatted AS STRINGS here, and the one
// sum the page computes itself (what a target change moves) is done in
// whole cents with BigInt — never through float arithmetic, which would
// disagree with the API's own half-up rounding (100.50 × 9% is 9.045:
// $9.05, where a float says 9.04).

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;
const MINUS = "−";

function parts(raw) {
  if (typeof raw !== "string") return null;
  const match = DECIMAL.exec(raw);
  if (!match) return null;
  return { negative: match[1] === "-", whole: match[2], fraction: match[3] ?? "" };
}

/** "1234567" → "1,234,567" (leading zeros dropped, "0" kept). */
function group(whole) {
  const digits = whole.replace(/^0+(?=\d)/, "");
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function isZero({ whole, fraction }) {
  return /^0+$/.test(whole) && /^0*$/.test(fraction);
}

/**
 * A dollar amount from its decimal string: "$1,234.56", at least two places
 * — a price keeps its places past the cent, without trailing zeros
 * ("$0.123456", "$123.45"). A negative amount reads "−$12.30". null when
 * it isn't a decimal string: the caller says "unknown", never a made-up
 * number.
 */
export function formatUsd(raw) {
  const p = parts(raw);
  if (!p) return null;
  let fraction = p.fraction.padEnd(2, "0");
  fraction = fraction.slice(0, 2) + fraction.slice(2).replace(/0+$/, "");
  const sign = p.negative && !isZero(p) ? MINUS : "";
  return `${sign}$${group(p.whole)}.${fraction}`;
}

/** Shares: "10", "0.123456", "1,250.5" — trailing zeros dropped. */
export function formatShares(raw) {
  const p = parts(raw);
  if (!p) return null;
  const fraction = p.fraction.replace(/0+$/, "");
  const sign = p.negative && !isZero(p) ? MINUS : "";
  return `${sign}${group(p.whole)}${fraction ? `.${fraction}` : ""}`;
}

/** A percentage exactly as the API gives it: "6.00%". */
export function formatPct(raw) {
  return parts(raw) ? `${raw}%` : null;
}

/** A drift in percentage points, signed: "+2.5000 pp", "−3.1000 pp". */
export function formatPoints(raw) {
  const p = parts(raw);
  if (!p) return null;
  const digits = `${p.whole}${p.fraction ? `.${p.fraction}` : ""}`;
  if (isZero(p)) return `${digits} pp`;
  return `${p.negative ? MINUS : "+"}${digits} pp`;
}

/**
 * A decimal string as a whole number of 1/10^places units (BigInt),
 * rounded half away from zero: scaled("6.00", 2) is 600n. null when it
 * isn't a decimal string.
 */
export function scaled(raw, places) {
  const p = parts(raw);
  if (!p) return null;
  const kept = p.fraction.slice(0, places).padEnd(places, "0");
  let units = BigInt(`${p.whole}${kept}`);
  const next = p.fraction.charAt(places);
  if (next && next >= "5") units += 1n;
  return p.negative ? -units : units;
}

/** Whole cents (BigInt) as dollars: "$1,234.56", "−$0.05". */
export function formatCents(cents) {
  const negative = cents < 0n;
  const digits = (negative ? -cents : cents).toString().padStart(3, "0");
  const whole = digits.slice(0, -2);
  const sign = negative ? MINUS : "";
  return `${sign}$${group(whole)}.${digits.slice(-2)}`;
}

/**
 * n / d rounded half away from zero (d > 0) — the API's ROUND_HALF_UP, on
 * BigInts.
 */
export function divideRounded(n, d) {
  const negative = n < 0n;
  const quotient = (2n * (negative ? -n : n) + d) / (2n * d);
  return negative ? -quotient : quotient;
}

/**
 * Roughly how much money a target change moves at a snapshot's total
 * value (spec §5.4, walkthrough 8): each changed holding's weight change
 * (percentage points) × the value, half up to the cent. `bySymbol` holds
 * the signed cents (a buy positive); `buys`, `sells` and `total` sum them.
 * Weights are % of the whole account (holdings + cash = 100), so this is
 * the change between the targets — a sync trades from the account's real
 * positions, which may have drifted. null without a value to measure at.
 *
 * @param {{symbol: string, weight_before: string, weight_after: string}[]} changes
 * @param {string|null} totalValue the snapshot's `total_value`
 */
export function moneyMoved(changes, totalValue) {
  const cents = scaled(totalValue, 2);
  if (cents === null) return null;
  const bySymbol = {};
  let buys = 0n;
  let sells = 0n;
  for (const change of changes) {
    const before = scaled(change.weight_before, 2);
    const after = scaled(change.weight_after, 2);
    if (before === null || after === null) continue;
    // Hundredths of a percent × cents / 10,000 = cents.
    const moved = divideRounded((after - before) * cents, 10000n);
    bySymbol[change.symbol] = moved;
    if (moved > 0n) buys += moved;
    else sells -= moved;
  }
  return { bySymbol, buys, sells, total: buys + sells };
}
